import {customElement, property} from "lit/decorators.js";
import {resolve} from "../../../container";
import {PageOverlayTrustPolicyProvider} from "./pageOverlay";
import {absoluteHrefFrom} from "../../../common/utils/url";
import {UnLitElement} from "../../../common/elements";
import {schedule} from "../../../common/utils/promises";
import {isInOverlayContext, type PageOverlayHandler, PageOverlayHandlerProvider} from "./pageOverlayHandler";
import {clickEventAlreadyHandledByChild} from "../../../common/utils/events";
import {ClickTracking} from "../../../tracking/clickTracking";
import {GLOBAL} from "../../../common/globals";

@customElement("eop-page-overlay-link")
export class EopPageOverlayLink extends UnLitElement {
    private handler: PageOverlayHandler;

    @property({attribute: "link-class", state: false})
    private linkClass: string;
    @property({attribute: "href"})
    private href: string;
    @property({attribute: "context-preserving", type: Boolean})
    private contextPreserving: boolean = false;

    public constructor(
        private handlerProvider: PageOverlayHandlerProvider = resolve(PageOverlayHandlerProvider),
        private pageOverlayTrustPolicyProvider: PageOverlayTrustPolicyProvider = resolve(PageOverlayTrustPolicyProvider),
        private tracking: ClickTracking = resolve(ClickTracking)
    ) {
        super();
    }

    public connectedCallback(): void {
        super.connectedCallback();
        void schedule(this.setupHandler()).as("page-overlay-link-init");

        // at the moment, the feature "context-preserving" is required only for page-overlay-links with direct href. To be precise: feed-preview tiles.
        if (this.href) {
            if (this.contextPreserving) {
                this.addClickOrEnterListener(ev => this.directClickListenerContextPreserving(ev));
            } else {
                this.addClickOrEnterListener(ev => this.directClickListener(ev));
            }
            this.setAttribute("tabindex", "0");
            this.style.cursor = "pointer";
        } else {
            const linkElement = this.querySelector<HTMLAnchorElement>(`.${this.linkClass}`);
            if (!linkElement) {
                return;
            }

            linkElement.addEventListener("click", ev => {
                this.innerElementClickListener(ev);
            });
        }
    }

    private addClickOrEnterListener(callback: (ev: Event) => void): void {
        this.addEventListener("click", e => {
            if (clickEventAlreadyHandledByChild(e, this)) {
                return;
            }
            callback(e);
        });
        this.addEventListener("keydown", ev => {
            if (clickEventAlreadyHandledByChild(ev, this)) {
                return;
            }
            if (ev.key === "Enter") {
                callback(ev);
            }
        });
    }

    private async setupHandler(): Promise<void> {
        this.handler = await this.handlerProvider.getPageOverlayHandler();
    }

    private directClickListener(ev: Event): void {
        this.openInOverlay(this.href, ev);
    }

    private directClickListenerContextPreserving(ev: Event): void {
        if (isInOverlayContext()) {
            this.openInOverlay(this.href, ev);
        } else {
            this.openLinkWithoutOverlay(this.href, "_self", ev);
        }
    }

    private innerElementClickListener(ev: Event): void {
        const clicked = Array.from(ev.composedPath())
            .filter(element => element instanceof HTMLElement)
            .map(element => element)
            .findFirst(elt => elt.classList.contains(this.linkClass))!;

        if (!clicked) {
            return;
        }

        const clickedLinkHref = clicked.getAttribute("href");
        if (!clickedLinkHref) {
            return;
        }

        this.openInOverlay(clickedLinkHref, ev);
    }

    private openInOverlay(clickedLinkHref: string, ev: Event): void {
        const absoluteHref = absoluteHrefFrom(clickedLinkHref);
        if (!this.pageOverlayTrustPolicyProvider.get().isTrustedHref(absoluteHref)) {
            this.openLinkWithoutOverlay(clickedLinkHref, "_blank", ev);
            return;
        }

        ev.preventDefault();
        ev.stopPropagation();
        this.tracking.handleBodyClick(ev as MouseEvent);
        this.handler.openPageInsideOverlay(absoluteHref);
    }

    private openLinkWithoutOverlay(clickedLinkHref: string, target: "_self" | "_blank", ev: Event): void {
        ev.preventDefault();
        ev.stopPropagation();
        this.tracking.handleBodyClick(ev as MouseEvent);
        GLOBAL.window().open(clickedLinkHref, target);
    }
}